import React, {
  createContext, useContext, useEffect, useRef, useState, useCallback,
} from 'react';
// eslint-disable-next-line @typescript-eslint/no-var-requires
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  ActiveCall, PhoneConfig, RegistrationState, ResolvedParty,
} from './types';
import {
  fetchPhoneConfig, resolveNumber, openCallLog, updateCallLog, uploadRecording,
  fetchAllowedCallers, setDnd as apiSetDnd,
} from './api';
import { CallRecorder } from './callRecorder';
import { CallStats } from './callStats';
import { MicBooster } from './micBooster';
import { TwilioEngine } from './twilioEngine';
import type { QueueNext } from './types';
import {
  getMicStream, Ringback, IncomingRing,
  getPreferredMicId, setPreferredMicId, listMicrophones,
  getPreferredSpeakerId, setPreferredSpeakerId, listSpeakers, applySpeaker,
  speakerSelectionSupported,
} from './audioConfig';

/**
 * PhoneContext — the softphone for the whole CRM session.
 *
 * Mounted once (inside the app shell) so one registration serves every page
 * and the call bar survives navigation.
 *
 * Twilio Voice is the only dial engine. The 019/JsSIP path was removed once
 * every employee had been moved across; what remains of it here is history in
 * the git log, not a second code path.
 *
 * The UI never touches the engine directly; it drives everything through this
 * context's imperative methods and reads the `call` state.
 */

interface PhoneContextValue {
  registration: RegistrationState;
  config: PhoneConfig | null;
  call: ActiveCall | null;
  dialerOpen: boolean;
  dnd: boolean;
  micGain: number;
  micDeviceId: string | null;
  microphones: { deviceId: string; label: string }[];
  speakerDeviceId: string | null;
  speakers: { deviceId: string; label: string }[];
  speakerSupported: boolean;
  // actions
  startCall: (number: string) => void;
  answer: () => void;
  hangup: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  addParticipant: (number: string) => Promise<void>; // Twilio 3-way
  queueWaiting: number;                               // Twilio inbound queue depth
  queueNext: QueueNext | null;                        // who is at the front (server-resolved)
  answerNext: () => Promise<void>;                    // pull next queued caller

  sendDtmf: (digit: string) => void;
  openDialer: () => void;
  closeDialer: () => void;
  toggleDnd: () => void;
  setMicGain: (g: number) => void;
  setMicDevice: (deviceId: string | null) => Promise<void>;
  refreshMicrophones: () => Promise<void>;
  setSpeaker: (deviceId: string | null) => Promise<void>;
  refreshSpeakers: () => Promise<void>;
  /** Rebuild the phone from scratch — what logging out and in used to be
   *  the only way to do. Refused mid-call (it would drop the call). */
  reconnect: () => void;
}

const PhoneContext = createContext<PhoneContextValue | null>(null);
export const usePhone = () => {
  const ctx = useContext(PhoneContext);
  if (!ctx) throw new Error('usePhone must be used within <PhoneProvider>');
  return ctx;
};

// Digits only, keep a leading + for international.
const cleanNumber = (n: string) => n.replace(/[^\d+*#]/g, '');


export const PhoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAgent } = useAuth();
  // Read inside the setCall callback below, which must not close over a stale
  // value; a ref keeps it current without re-creating answer().
  const isAgentRef = useRef(isAgent);
  isAgentRef.current = isAgent;
  const toast = useToast();
  const navigate = useNavigate();

  const sessionRef = useRef<any>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const recorderRef = useRef<CallRecorder | null>(null);
  const statsRef = useRef<CallStats | null>(null);
  const boosterRef = useRef<MicBooster | null>(null);
  // The Twilio dial engine — the only one.
  // "twilio". Only one engine is live at a time (chosen by config.provider).
  const twilioRef = useRef<TwilioEngine | null>(null);
  // Twilio call bookkeeping: answered? finalized already? current direction —
  // so onState() stamps answeredAt, uploads the recording, logs the right
  // status, and never double-finalizes (the SDK 'disconnect' + a manual hangup
  // both reach the 'ended' state).
  const twilioAnsweredRef = useRef(false);
  const twilioEndedRef = useRef(false);
  const currentDirectionRef = useRef<'inbound' | 'outbound'>('outbound');
  // Local ringback tone for outbound calls (see Ringback in audioConfig).
  const ringbackRef = useRef<Ringback>(new Ringback());
  // What the rep hears when a call arrives (Twilio inbound, a
  // queue caller reaching the front). Ringback is the OUTBOUND dialing tone.
  const incomingRingRef = useRef<IncomingRing>(new IncomingRing());
  // Resolves to the server CallLog id once POST /calls returns. Lets the
  // recording upload wait for it even if the call ends before the id is
  // stored in call.logId (short calls on a slow network).
  const logIdPromiseRef = useRef<Promise<string | null> | null>(null);

  const [config, setConfig] = useState<PhoneConfig | null>(null);
  const [registration, setRegistration] = useState<RegistrationState>('unconfigured');
  // Bumped by reconnect(): re-runs the registration effect, which destroys the
  // Twilio device and builds a new one with a fresh token.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const [call, setCall] = useState<ActiveCall | null>(null);
  // Ref mirror of `call` so imperative actions (answer/hangup) can read the
  // current call and its stashed Twilio accept/reject WITHOUT putting side
  // effects inside a setCall updater (updaters must stay pure).
  const callRef = useRef<ActiveCall | null>(null);
  callRef.current = call;
  const [dialerOpen, setDialerOpen] = useState(false);
  const [dnd, setDnd] = useState(false);
  // Outbound mic gain (how loud the customer hears the rep). Persisted per
  // browser so a rep's preference survives calls and reloads. 1.0 = unity.
  const [micGain, setMicGainState] = useState<number>(() => {
    const saved = Number(localStorage.getItem('telephony.micGain'));
    return Number.isFinite(saved) && saved >= 0.5 && saved <= 3 ? saved : 1.5;
  });
  // Ref mirror so a stable callback always
  // reads the latest gain when it creates a call's booster.
  const micGainRef = useRef(micGain);
  micGainRef.current = micGain;
  // Chosen microphone (deviceId) + the list to pick from. Persisted in
  // audioConfig so getMediaConstraints() picks it up on the next call.
  const [micDeviceId, setMicDeviceIdState] = useState<string | null>(() => getPreferredMicId());
  const [microphones, setMicrophones] = useState<{ deviceId: string; label: string }[]>([]);
  // Speaker (output) — where the rep hears the customer. Applied to the remote
  // <audio> element via setSinkId.
  const [speakerDeviceId, setSpeakerDeviceIdState] = useState<string | null>(() => getPreferredSpeakerId());
  const [speakers, setSpeakers] = useState<{ deviceId: string; label: string }[]>([]);
  const speakerSupported = speakerSelectionSupported();
  // Twilio inbound queue: how many callers are waiting (polled while idle).
  const [queueWaiting, setQueueWaiting] = useState(0);
  const [queueNext, setQueueNext] = useState<QueueNext | null>(null);
  const queueNextRef = useRef<QueueNext | null>(null);
  queueNextRef.current = queueNext; // sync-on-render mirror, like callRef below
  // Queue callers the rep explicitly declined (rejected the ring) — don't
  // ring them again for this rep; they stay in the queue for everyone else.
  const dismissedQueueRef = useRef<Set<string>>(new Set());
  // Inbound-screening state, read inside the inbound handler (refs so
  // the handler always sees the latest without re-registering).
  const screeningRef = useRef(false);
  const allowedKeysRef = useRef<Set<string>>(new Set());
  const dndRef = useRef(false);

  // Attach recorder + quality stats + mic booster to the live Twilio peer
  // connection. Called on ANSWER (when media is negotiated), not at connect().
  const attachTwilioMedia = useCallback(async () => {
    const pc = twilioRef.current?.getPeerConnection();
    if (!pc) return;
    // NOTE: recording for Twilio is done SERVER-SIDE on the conference (so it
    // captures a 3-way third leg too), so we do NOT start the client recorder
    // here. Stats (browser-leg quality) + mic booster still attach.
    try { if (!boosterRef.current) { boosterRef.current = new MicBooster(micGainRef.current); await boosterRef.current.apply(pc); } } catch { boosterRef.current = null; }
    try { if (!statsRef.current) { statsRef.current = new CallStats(); statsRef.current.start(pc); } } catch { statsRef.current = null; }
  }, []);




  // Stop recording and upload to the call's log (best-effort, off the UI
  // path). If the CallLog id isn't ready yet (call ended before POST
  // /calls resolved), await the pending promise instead of dropping the
  // captured audio.
  const stopAndUploadRecording = useCallback((logId?: string) => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (!rec) return;
    const idReady = logId ? Promise.resolve(logId) : (logIdPromiseRef.current || Promise.resolve(null));
    Promise.all([rec.stop(), idReady])
      .then(([blob, id]) => {
        if (blob && blob.size > 0 && id) return uploadRecording(id, blob);
      })
      .catch(() => { /* recording is best-effort */ });
  }, []);


  // End the call: update the log, upload the recording, flash the ended
  // state briefly, then idle.
  const finalizeCall = useCallback((status: string) => {
    ringbackRef.current.stop(); // call is over — never leave the tone ringing
    incomingRingRef.current.stop();
    // Capture the audio-quality summary before tearing the sampler down, so
    // it rides along on the final PATCH for cross-call review.
    const audioStats = statsRef.current?.summary() || undefined;
    statsRef.current?.stop(); statsRef.current = null;
    boosterRef.current?.dispose(); boosterRef.current = null;
    setCall(prev => {
      if (!prev) return null;
      const durationSec = prev.answeredAt ? Math.round((Date.now() - prev.answeredAt) / 1000) : 0;
      // Resolve the CallLog id (await the pending POST if it hasn't
      // landed in prev.logId yet) so the status update — and its
      // server-side missed-call reminder — isn't lost on a short call.
      const idReady = prev.logId ? Promise.resolve(prev.logId) : (logIdPromiseRef.current || Promise.resolve(null));
      idReady.then(id => { if (id) updateCallLog(id, { status, durationSec, audioStats }).catch(() => {}); });
      // Only answered calls have audio worth uploading.
      if (prev.answeredAt) stopAndUploadRecording(prev.logId);
      else { recorderRef.current?.stop().catch(() => {}); recorderRef.current = null; }
      return { ...prev, state: 'ended' };
    });
    sessionRef.current = null;
    logIdPromiseRef.current = null;
    window.setTimeout(() => setCall(null), 1200);
  }, [stopAndUploadRecording]);

  // ── Register with the PBX once we have the user's SIP config ──────
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    fetchPhoneConfig().then(async cfg => {
      if (cancelled) return;
      setConfig(cfg);

      // The Twilio engine needs no
      // SIP extension/wssUri — the identity is crm_<userId> on the server.
      if (cfg.configured && cfg.provider === 'twilio') {
        // Seed screening/DND SYNCHRONOUSLY before the engine can receive an
        // inbound call — otherwise the whitelist would be bypassed until the
        // 30s refresh first runs.
        setDnd(!!cfg.dnd);
        dndRef.current = !!cfg.dnd;
        screeningRef.current = !!cfg.screeningEnabled;
        try {
          const callers = await fetchAllowedCallers();
          allowedKeysRef.current = new Set(callers.map(c => c.numberKey));
        } catch { /* keep whatever we had */ }
        if (cancelled) return;
        setRegistration('connecting');
        // A queue pull: 'active' fires when OUR leg joins our own conference —
        // the caller is redirected in a moment later. Poll the server for the
        // bridge; only then is this an answered call. Until then the call
        // carries no logId, so a leg that drops first PATCHes nothing and the
        // server decides that row's fate (release / missed).
        const awaitBridge = async () => {
          const deadline = Date.now() + 25_000;
          let identified = false;
          while (Date.now() < deadline) {
            if (twilioEndedRef.current) return;
            const p = await TwilioEngine.pulled();
            if (twilioEndedRef.current) return;
            if (!p) {
              // Nothing claimed for us (empty pull, someone else won, lease lost).
              // The server's TwiML hangs this leg up; treat as never-connected.
              twilioAnsweredRef.current = false;
              try { twilioRef.current?.hangup(); } catch { /* already gone */ }
              toast.info('השיחה כבר נענתה על ידי נציג אחר');
              return;
            }
            if (!identified) {
              identified = true;
              setCall(c => c ? { ...c, number: p.number, party: p.party } as any : c);
            }
            if (p.ended) { try { twilioRef.current?.hangup(); } catch { /* noop */ } return; }
            if (p.bridged) {
              twilioAnsweredRef.current = true;
              logIdPromiseRef.current = Promise.resolve(p.logId);
              setCall(c => c ? { ...c, logId: p.logId, state: 'active', answeredAt: c.answeredAt || Date.now() } as any : c);
              return;
            }
            await new Promise(r => setTimeout(r, 1000));
          }
          // The server gives the caller back and ends our empty conference.
          toast.error('לא הצלחנו לחבר את המתקשר — נסה שוב');
          try { twilioRef.current?.hangup(); } catch { /* noop */ }
        };

        try {
          const engine = new TwilioEngine({
            onReady: () => setRegistration('registered'),
            // The engine recovers by itself (fresh token + re-register, with
            // backoff). These only keep the UI honest: before, a dead device
            // still showed as registered until the rep logged out and in.
            onReconnecting: () => setRegistration('reconnecting'),
            onDisconnected: () => setRegistration('failed'),
            onError: () => { /* per-call errors surface in the call UI */ },
            onState: (s) => {
              if (s === 'active') {
                // Media is negotiated now (not at connect() time) — attach
                // recorder/stats/boost here for both kinds of call.
                attachTwilioMedia();
                if ((callRef.current as any)?.pulled) {
                  // A queue pull: only OUR leg is in so far. Not answered until
                  // the server reports the caller bridged — see awaitBridge.
                  logIdPromiseRef.current = null;
                  void awaitBridge();
                  return;
                }
                // Answered: stamp answeredAt (so duration + recording upload
                // behave the same way).
                twilioAnsweredRef.current = true;
                setCall(c => c ? { ...c, state: 'active', answeredAt: c.answeredAt || Date.now() } as any : c);
                setCall(c => { if (c?.logId) updateCallLog(c.logId, { status: 'answered' }).catch(() => {}); return c; });
                return;
              }
              if (s === 'ended') {
                // The SDK 'disconnect' event and a user hangup() can both land
                // here; finalize once. Status reflects whether it was answered.
                if (twilioEndedRef.current) return;
                twilioEndedRef.current = true;
                finalizeCall(twilioAnsweredRef.current ? 'completed'
                  : (currentDirectionRef.current === 'inbound' ? 'missed' : 'canceled'));
                return;
              }
              // ringing / connecting → just reflect the dialer state.
              setCall(c => c ? { ...c, state: s === 'ringing' ? 'ringing' : 'dialing' } : c);
            },
            onIncoming: async (from, accept, reject) => {
              const number = from.replace(/^\+972/, '0').replace(/^\+/, '');
              // (Screening + DND for Twilio are enforced server-side in /voice —
              // PSTN callers are queued there and never reach this handler, which
              // only sees direct client-to-client calls.)
              if (callRef.current) { reject(); return; } // already on a call
              // Reset per-call bookkeeping for this new inbound call.
              twilioAnsweredRef.current = false;
              twilioEndedRef.current = false;
              currentDirectionRef.current = 'inbound';
              let party: ResolvedParty | undefined;
              try { party = await resolveNumber(number); } catch { /* unknown */ }
              const logP = openCallLog('inbound', number).then(r => r.id).catch(() => null);
              logIdPromiseRef.current = logP;
              incomingRingRef.current.start();
              setCall({
                direction: 'inbound', number, state: 'ringing', muted: false,
                customer: party?.customer, deal: party?.deal, startedAt: Date.now(),
                // Stash accept/reject so answer()/hangup() can drive them.
                _twilioAccept: accept, _twilioReject: reject,
              } as any);
              logP.then(id => { if (id) setCall(c => c ? { ...c, logId: id } as any : c); });
            },
          });
          // Expose the engine BEFORE init resolves: "registered" fires inside
          // init(), the UI then shows the queue, and a click in that window
          // used to hit a null ref and do nothing at all.
          twilioRef.current = engine;
          await engine.init();
        } catch {
          setRegistration('failed');
        }
        return;
      }

      // Without a Twilio extension there is nothing to register.
      setRegistration('unconfigured');
    }).catch(() => {
      if (!cancelled) setRegistration('failed');
    });

    const ringback = ringbackRef.current;
    const incomingRing = incomingRingRef.current;
    return () => {
      cancelled = true;
      try { twilioRef.current?.destroy(); } catch { /* noop */ }
      twilioRef.current = null;
      ringback.dispose();
      incomingRing.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, reconnectNonce]);

  // Keep the phone registered through sleep, network drops and background
  // tabs. The SDK renews its token from a timer that a sleeping laptop or a
  // throttled tab misses; the device then dies with an expired token. Check
  // whenever the rep comes back, when the network returns, and every minute.
  useEffect(() => {
    if (!user) return;
    const check = () => { void twilioRef.current?.checkHealth(); };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', check);
    window.addEventListener('focus', check);
    const t = window.setInterval(check, 60_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', check);
      window.removeEventListener('focus', check);
      window.clearInterval(t);
    };
  }, [user]);

  const reconnect = useCallback(() => {
    if (callRef.current) {
      toast.error('אי אפשר לחבר מחדש באמצע שיחה');
      return;
    }
    setRegistration('connecting');
    setReconnectNonce((n) => n + 1);
  }, [toast]);

  // Poll the inbound queue depth for a Twilio rep while they're idle, so the
  // UI can offer "answer next" when callers are waiting. Stops during a call.
  // (Deliberately NOT gated on DND: a rep on DND stops NEW callers being parked
  // — the server enforces that — but must still be able to see and take the
  // people already waiting, or the last rep flipping DND would strand them.)
  //
  // RINGING: the queue itself is silent (Twilio parks the caller), so a caller
  // reaching the front is surfaced exactly like any inbound call — ring tone
  // + the incoming-call card with their name. That card is a local placeholder
  // (`queued: true`, no Twilio call yet): "answer" pulls them from the queue,
  // "reject" dismisses the ring for this rep only. Every idle rep rings; the
  // claim is atomic on the server, so the first to answer wins and the others
  // are told the call was taken.
  useEffect(() => {
    const clear = () => { setQueueWaiting(0); setQueueNext(null); };
    if (config?.provider !== 'twilio') { clear(); return; }
    let stopped = false;
    const tick = async () => {
      // Read the live call through the ref: the interval is created once, so
      // a captured `call` would be stale within seconds.
      const active = callRef.current as any;
      if (active && !active.queued) { clear(); return; } // mid-call: a ring placeholder is not a call
      const s = await TwilioEngine.queueStatus();
      if (stopped) return;
      setQueueWaiting(s.waiting);
      // Same caller as last tick → keep the same object so nothing re-renders.
      setQueueNext(prev => (prev?.logId === s.next?.logId ? prev : s.next));

      const cur = callRef.current as any;
      if (cur?.queued) {
        // Ringing for someone: stop if they're gone (answered elsewhere / hung
        // up) or the rep went on DND.
        if (!s.next || s.next.logId !== cur.queueLogId || dndRef.current) {
          incomingRingRef.current.stop();
          setCall(null);
        }
        return;
      }
      if (!callRef.current && s.next && !dndRef.current && !dismissedQueueRef.current.has(s.next.logId)) {
        incomingRingRef.current.start();
        setCall({
          direction: 'inbound', number: s.next.number, party: s.next.party,
          state: 'ringing', muted: false, startedAt: Date.now(),
          queued: true, queueLogId: s.next.logId,
        } as any);
      }
    };
    tick();
    const id = window.setInterval(tick, 5000);
    return () => { stopped = true; window.clearInterval(id); };
    // Depends on provider only — NOT on `call`. Including the call object tore
    // the interval down and rebuilt it on every state change, and each rebuild
    // fired tick() immediately, so a ringing queue placeholder produced a
    // burst of /queue-depth requests instead of one every 5s. The current call
    // is read through callRef inside tick(), which is always up to date.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.provider]);

  // Keep the screening switch + whitelist fresh WITHOUT re-registering the
  // phone, so an admin adding a number takes effect within ~30s instead of
  // only after the affected rep reloads the page.
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const refresh = async () => {
      try {
        const [cfg, callers] = await Promise.all([fetchPhoneConfig(), fetchAllowedCallers()]);
        if (stopped) return;
        screeningRef.current = !!cfg.screeningEnabled;
        allowedKeysRef.current = new Set(callers.map(c => c.numberKey));
        // DND is user-owned locally; only adopt server value if it drifted
        // and we're not the one who just toggled (best-effort).
        if (typeof cfg.dnd === 'boolean' && cfg.dnd !== dndRef.current) {
          dndRef.current = cfg.dnd;
          setDnd(cfg.dnd);
        }
      } catch { /* keep current */ }
    };
    const t = window.setInterval(refresh, 30_000);
    return () => { stopped = true; window.clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Imperative actions ───────────────────────────────────────────
  const startCall = useCallback(async (input: string) => {
    const number = cleanNumber(input);
    if (!number) return;

    // ── Twilio engine path ──────────────────────────────────────────
    if (twilioRef.current) {
      if (registration !== 'registered') {
        // Kick a reconnect now rather than waiting for the next health check.
        void twilioRef.current.checkHealth();
        toast.error('המרכזייה מתחברת מחדש — נסה שוב בעוד כמה שניות');
        return;
      }
      if (call) { toast.error('כבר מתקיימת שיחה'); return; }
      // Reset per-call bookkeeping (recording/stats attach on ANSWER, not now).
      twilioAnsweredRef.current = false;
      twilioEndedRef.current = false;
      currentDirectionRef.current = 'outbound';
      let party: ResolvedParty | undefined;
      try { party = await resolveNumber(number); } catch { /* unknown */ }
      const logP = openCallLog('outbound', number).then(r => r.id).catch(() => null);
      logIdPromiseRef.current = logP;
      setCall({
        direction: 'outbound', number, state: 'dialing', muted: false,
        customer: party?.customer, deal: party?.deal,
        logId: undefined, startedAt: Date.now(),
      } as any);
      logP.then(id => { if (id) setCall(c => c ? { ...c, logId: id } as any : c); });
      setDialerOpen(false);
      try {
        // Israel: leading 0 → +972 so Twilio dials E.164.
        let to = number; if (to.startsWith('0')) to = '+972' + to.slice(1);
        // Await the CallLog id so it can ride along to the /voice webhook and
        // link the conference recording back to this call.
        const logId = await logP;
        await twilioRef.current.call(to, logId || undefined);
        // Media (recorder/stats/boost) is attached on onState('active') —
        // when the callee answers and the PC actually has media.
      } catch { toast.error('החיוג נכשל'); finalizeCall('failed'); }
      return;
    }

    // Nothing else can dial: the Twilio branch above is the only engine and it
    // returns. Reaching here means the phone never registered.
    toast.error('הטלפון לא מחובר');
  }, [registration, config, toast]);

  // Pull the front-of-queue caller. Shared by the FAB ("answer next") and the
  // ring card's answer button (which replaces its placeholder with this).
  const pullFromQueue = useCallback(async () => {
    if (!twilioRef.current) { toast.error('הטלפון עדיין מתחבר — נסה שוב בעוד רגע'); return; }
    incomingRingRef.current.stop();
    // The server opened this caller's CallLog when they were enqueued (real
    // number, resolved customer) and owns it until they're bridged — so we
    // open nothing here and carry NO logId until 'active' confirms who we
    // got. A pull that fails or is abandoned therefore patches nothing; the
    // server's queue/conference webhooks decide that row's fate.
    const placeholder = callRef.current as any;
    const next = queueNextRef.current;
    const number = placeholder?.queued ? placeholder.number : next?.number;
    const party = placeholder?.queued ? placeholder.party : next?.party;
    twilioAnsweredRef.current = false;
    twilioEndedRef.current = false;
    currentDirectionRef.current = 'inbound';
    // `pulled` rides on the call itself (so it dies with it) and tells the
    // 'active' handler to adopt the server-confirmed identity. The preview
    // (number/party) is the queue front as of the last poll; the server
    // corrects it if someone else took that caller first.
    setCall({
      direction: 'inbound', number: number || 'שיחה מהתור', party,
      state: 'dialing', muted: false, startedAt: Date.now(), pulled: true,
    } as any);
    try {
      await twilioRef.current.pullQueue();
    } catch (e: any) {
      toast.error(/not ready/i.test(e?.message || '') ? 'הטלפון עדיין מתחבר — נסה שוב בעוד רגע' : 'אין שיחה בתור');
      finalizeCall('canceled');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  const answer = useCallback(() => {
    incomingRingRef.current.stop();
    // A ringing QUEUE placeholder: there is no Twilio call yet — pull them.
    if ((callRef.current as any)?.queued) { pullFromQueue(); return; }
    // Twilio inbound: accept the stashed call. The side-effect (accept) runs
    // OUTSIDE setCall — a state updater must be pure. onState('active') then
    // stamps answeredAt + attaches media, so nothing else is needed here.
    if (twilioRef.current) {
      (callRef.current as any)?._twilioAccept?.();
      return;
    }
    // Nothing else can answer: sessionRef was only ever populated by the
    // removed SIP path, and the Twilio branch above returns.
  }, [navigate, config, pullFromQueue]);

  const hangup = useCallback(() => {
    incomingRingRef.current.stop();
    const q = callRef.current as any;
    if (q?.queued) {
      // Declining the ring: nothing to tear down on Twilio's side — the caller
      // is still in the queue for everyone else. Just don't ring THIS rep for
      // them again.
      incomingRingRef.current.stop();
      if (q.queueLogId) dismissedQueueRef.current.add(q.queueLogId);
      setCall(null);
      return;
    }
    if (twilioRef.current) {
      const c = callRef.current;
      // A ringing inbound call that was never answered → reject it.
      if (c?.direction === 'inbound' && c.state === 'ringing') (c as any)?._twilioReject?.();
      // Just tear down the SDK call; its 'disconnect' event drives finalizeCall
      // via onState('ended') — which is guarded against double-finalize. Do NOT
      // finalize here too (that was a duplicate upload + status PATCH).
      twilioRef.current.hangup();
      return;
    }
    const s = sessionRef.current;
    try { s?.terminate(); } catch { /* already gone */ }
    if (!s) setCall(null);
  }, []);

  const toggleMute = useCallback(() => {
    if (twilioRef.current) {
      // Side effect OUTSIDE the updater (updaters must be pure).
      const next = !(callRef.current?.muted);
      twilioRef.current.mute(next);
      setCall(c => c ? { ...c, muted: next } : c);
      return;
    }
    const s = sessionRef.current;
    if (!s) return;
    setCall(c => {
      if (!c) return c;
      const next = !c.muted;
      try { next ? s.mute({ audio: true }) : s.unmute({ audio: true }); } catch { /* noop */ }
      return { ...c, muted: next };
    });
  }, []);

  // Set the outbound mic gain: apply live to the current call and persist so
  // the next call (and a reload) starts at the same level.
  const setMicGain = useCallback((g: number) => {
    const clamped = Math.min(3, Math.max(0.5, g));
    boosterRef.current?.setGain(clamped);
    try { localStorage.setItem('telephony.micGain', String(clamped)); } catch { /* noop */ }
    setMicGainState(clamped);
  }, []);

  // Choose which microphone to use (deviceId). Persisted, and applied LIVE to
  // an in-progress call (not just the next one) — reps change device mid-call
  // and expect it to take effect. null = system default.
  const setMicDevice = useCallback(async (deviceId: string | null) => {
    setPreferredMicId(deviceId);
    setMicDeviceIdState(deviceId);

    // Twilio: the SDK owns the input device — switch it there (applies live).
    if (twilioRef.current) { await twilioRef.current.setMicDevice(deviceId); return; }

    // If a call is active, re-acquire the mic on the new device and swap it
    // onto the peer connection. Route it back through the gain booster so the
    // volume setting is preserved.
    const pc: RTCPeerConnection | undefined = sessionRef.current?.connection;
    const sender = pc?.getSenders().find((s) => s.track && s.track.kind === 'audio');
    if (!pc || !sender) return;
    try {
      // Tear down the old booster (restores/frees the previous track graph).
      await boosterRef.current?.dispose(); boosterRef.current = null;
      // Fresh stream from the chosen device (falls back to default if gone).
      const stream = await getMicStream();
      const track = stream.getAudioTracks()[0];
      if (!track) return;
      await sender.replaceTrack(track);
      // Re-boost the new mic at the current gain.
      boosterRef.current = new MicBooster(micGainRef.current);
      await boosterRef.current.apply(pc);
    } catch {
      // Leave whatever track is currently live; never break the call.
    }
  }, []);

  // (Re)enumerate microphones for the picker.
  const refreshMicrophones = useCallback(async () => {
    setMicrophones(await listMicrophones());
  }, []);

  // Choose the speaker (output). Applies LIVE to the current call's audio
  // element (setSinkId is instant) and persists for future calls.
  const setSpeaker = useCallback(async (deviceId: string | null) => {
    setPreferredSpeakerId(deviceId);
    setSpeakerDeviceIdState(deviceId);
    await applySpeaker(remoteAudioRef.current);
  }, []);

  const refreshSpeakers = useCallback(async () => {
    setSpeakers(await listSpeakers());
  }, []);

  const toggleHold = useCallback(() => {
    // Twilio: hold is server-side (conference participant hold on the customer).
    if (twilioRef.current) {
      const held = callRef.current?.state === 'held';
      twilioRef.current.hold(!held).catch(() => toast.error('העברה להמתנה נכשלה'));
      setCall(c => c ? { ...c, state: held ? 'active' : 'held' } : c);
      return;
    }
    const s = sessionRef.current;
    if (!s) return;
    setCall(c => {
      if (!c) return c;
      const held = c.state === 'held';
      try { held ? s.unhold() : s.hold(); } catch { /* noop */ }
      return { ...c, state: held ? 'active' : 'held' };
    });
  }, [toast]);

  // Answer the next caller waiting in the Twilio inbound queue.
  const answerNext = useCallback(async () => {
    const c = call as any;
    if (call && !c?.queued) { toast.error('כבר מתקיימת שיחה'); return; }
    await pullFromQueue();
  }, [call, toast, pullFromQueue]);

  // Add a 3rd participant to the call.
  const addParticipant = useCallback(async (number: string) => {
    if (!twilioRef.current) { toast.error('זמין רק ב-Twilio'); return; }
    try {
      await twilioRef.current.addParticipant(number);
      toast.success('המשתתף מתווסף לשיחה');
    } catch {
      toast.error('הוספת המשתתף נכשלה');
    }
  }, [toast]);

  const sendDtmf = useCallback((digit: string) => {
    if (twilioRef.current) { twilioRef.current.sendDigits(digit); return; }
    try { sessionRef.current?.sendDTMF(digit); } catch { /* noop */ }
  }, []);

  // Toggle this employee's do-not-disturb (optimistic, persisted).
  const toggleDnd = useCallback(() => {
    setDnd(prev => {
      const next = !prev;
      dndRef.current = next;
      apiSetDnd(next).catch(() => {
        // revert on failure
        dndRef.current = prev;
        setDnd(prev);
        toast.error('שמירת מצב "נא לא להפריע" נכשלה');
      });
      return next;
    });
  }, [toast]);

  const value: PhoneContextValue = {
    registration, config, call, dialerOpen, dnd, micGain, micDeviceId, microphones,
    speakerDeviceId, speakers, speakerSupported,
    startCall, answer, hangup, toggleMute, toggleHold, addParticipant, sendDtmf,
    queueWaiting, queueNext, answerNext,
    openDialer: () => setDialerOpen(true),
    closeDialer: () => setDialerOpen(false),
    toggleDnd, setMicGain, setMicDevice, refreshMicrophones,
    setSpeaker, refreshSpeakers,
    reconnect,
  };

  return (
    <PhoneContext.Provider value={value}>
      {children}
      {/* Remote party audio — always mounted, hidden. */}
      <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
    </PhoneContext.Provider>
  );
};
